// New Year's fireworks all around the stadium, over the lake, the forest and both ends: bursts of glowing sparks that bloom, droop and fade, each
// rocket at its own pace. One Points draw; every spark's path is worked out in the shader.
import { AdditiveBlending, BufferAttribute, BufferGeometry, Points, ShaderMaterial, Uniform } from 'three';
import { TRACK_LENGTH } from '../../data/track';
import { rng } from './layout';

const ROCKETS = 14;
const SPARKS = 160;

export class Fireworks {
  readonly points: Points<BufferGeometry, ShaderMaterial>;
  private uniforms = { uTime: new Uniform(0), uAmount: new Uniform(1), uScale: new Uniform(600) };

  constructor() {
    const r = rng(2026);
    const n = ROCKETS * SPARKS;
    const pos = new Float32Array(n * 3), seed = new Float32Array(n * 2);
    for (let i = 0; i < n; i++) {
      // A random direction on the sphere for each spark, and its rocket's number.
      const u = r() * 2 - 1, a = r() * Math.PI * 2, k = Math.sqrt(1 - u * u);
      pos.set([k * Math.cos(a), u, k * Math.sin(a)], i * 3);
      seed.set([Math.floor(i / SPARKS), r()], i * 2);
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(pos, 3));
    g.setAttribute('aSeed', new BufferAttribute(seed, 2));
    const mat = new ShaderMaterial({
      uniforms: this.uniforms, transparent: true, depthWrite: false, blending: AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute vec2 aSeed;
        uniform float uTime, uAmount, uScale;
        varying vec3 vColour;
        varying float vA;
        float h(float n) { return fract(sin(n * 127.1 + 311.7) * 43758.5453); }
        void main() {
          float rocket = aSeed.x;
          float period = 3.2 + h(rocket) * 3.0;
          float t = uTime + h(rocket + 9.0) * period;
          float shot = floor(t / period);
          float age = t - shot * period;
          float id = rocket * 17.0 + shot;
          // On a ring round the course, so every camera sees some of them above the trees.
          float ang = h(id) * 6.2831;
          float ring = 170.0 + h(id + 2.0) * 160.0;
          vec3 centre = vec3(${(TRACK_LENGTH / 2).toFixed(1)} + cos(ang) * (ring + ${(TRACK_LENGTH / 2).toFixed(1)}),
                             60.0 + h(id + 1.0) * 70.0, sin(ang) * ring);
          float speed = 18.0 + h(id + 3.0) * 12.0;
          float spread = speed * (1.0 - exp(-age * 2.2)) / 2.2 * (0.85 + aSeed.y * 0.3);
          vec3 wp = centre + position * spread - vec3(0.0, 2.2 * age * age, 0.0);
          float kind = h(id + 4.0);
          vec3 c1 = kind < 0.25 ? vec3(1.0, 0.25, 0.2) : kind < 0.5 ? vec3(0.3, 0.6, 1.0) : kind < 0.75 ? vec3(1.0, 0.8, 0.3) : vec3(0.7, 1.0, 0.5);
          vec3 c2 = h(id + 5.0) < 0.5 ? vec3(1.0) : c1.gbr;
          vColour = mix(c1, c2, step(0.6, aSeed.y)) * 6.0;
          float life = 2.4;
          vA = uAmount * (1.0 - smoothstep(life * 0.5, life, age)) * step(age, life)
             * (0.7 + 0.3 * sin(age * 30.0 + aSeed.y * 40.0));
          vec4 mv = viewMatrix * vec4(wp, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = clamp(0.7 * uScale / max(1.0, -mv.z), 2.0, 8.0);
        }`,
      fragmentShader: /* glsl */ `
        varying vec3 vColour;
        varying float vA;
        void main() {
          float a = smoothstep(0.5, 0.0, length(gl_PointCoord - 0.5)) * vA;
          if (a < 0.01) discard;
          gl_FragColor = vec4(vColour * a, a);
        }`,
    });
    this.points = new Points(g, mat);
    this.points.frustumCulled = false;
    this.points.name = 'fireworks';
  }

  /** 0 hides the fireworks (daylight), 1 shows them. */
  set amount(v: number) {
    this.uniforms.uAmount.value = v;
    this.points.visible = v > 0.01;
  }

  update(timeS: number, fovDeg: number, viewportHeight: number): void {
    this.uniforms.uTime.value = timeS;
    this.uniforms.uScale.value = viewportHeight / (2 * Math.tan((fovDeg * Math.PI) / 360));
  }

  dispose(): void {
    this.points.geometry.dispose();
    this.points.material.dispose();
  }
}
